import { gql } from "graphql-request";

export type User = {
  firstName: string;
  lastName: string;
  mobileNumber: string;
  country: string;
  dob: string;
  timezone: string;
  emailVerification?: {
    verified?: boolean | null;
  } | null;
};

export const normalizeUserDob = (dob?: string | Date | null) => {
  if (!dob) {
    return "";
  }

  if (dob instanceof Date) {
    return dob.toISOString().split("T")[0] || "";
  }

  return dob.split("T")[0] || "";
};

export const normalizeUserProfile = <T extends { dob?: string | Date | null }>(user: T) => {
  return {
    ...user,
    dob: normalizeUserDob(user.dob),
  };
};

export const getEmailVerificationState = (
  user?: { emailVerification?: { verified?: boolean | null } | null } | null,
) => {
  const verified = user?.emailVerification?.verified;

  return typeof verified === "boolean" ? verified : null;
};

const serializeUserDob = (dob?: string | Date | null) => {
  if (!dob) {
    return undefined;
  }

  if (dob instanceof Date) {
    return dob.toISOString();
  }

  const trimmedDob = dob.trim();
  if (!trimmedDob) {
    return undefined;
  }

  if (trimmedDob.includes("T")) {
    const parsedDate = new Date(trimmedDob);
    return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate.toISOString();
  }

  const parsedDate = new Date(`${trimmedDob}T00:00:00.000Z`);
  return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate.toISOString();
};

export type UserFeedBack = {
  UserId: string;
  Feedback: string;
  files?: Array<File>;
  ContactMe: boolean;
};

const gqlRequest = async (query: string, variables = {}) => {
  const { $gqlRequest } = useNuxtApp();
  return await ($gqlRequest as any)(query, variables);
};

export const getUserDetails = async (userId: string) => {
  const query = gql`
    query Me ($userId: String!) {
      user (query: {
        userId: $userId
      }) {
        userId
        email
        firstName
        lastName
        mobileNumber
        timezone
        dob
        country
        emailVerification {
          verified
        }
        maxCallRecLengthCanTranscribe
        tos {
            accepted
            acceptedAt
            version
        }
        settingsData {
          call_using
          default_bpnumber_id
        }
      }
    }
  `;

  const variables = {
    userId,
  };

  const data = await gqlRequest(query, variables);
  return data;
};

export const updateUserProfile = async (userId: string, userDetails: User) => {
  const query = gql`
    mutation updateUserNames($user: UserUpdateInput!, $userId: String) {
      updateOneUser(query: { userId: $userId }, set: $user) {
        firstName
        lastName
        mobileNumber
        dob
        timezone
        country
      }
    }
  `;

  const serializedDob = serializeUserDob(userDetails.dob);
  const userPayload = {
    ...userDetails,
    dob: serializedDob,
  };

  if (!serializedDob) {
    delete userPayload.dob;
  }

  const variables = {
    userId: userId,
    user: userPayload,
  };

  const data = await gqlRequest(query, variables);
  return data;
};
